import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  Alert,
  Card,
  CardContent
} from '@mui/material';
import { Icon } from '@iconify/react';
import { debounceFunc } from 'src/utils/helper';
import { LocationList, LocationDetails, LocationForm } from '../../components/ocpi/locations';
// hooks
import { useFetchOnDemand } from 'src/hooks/useFetch.hook';
import { useDropdown } from 'src/hooks/useDropdown.hook';


const OCPILocationsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partyFilter, setPartyFilter] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: locations, isLoading, mutate: fetchLocations } = useFetchOnDemand("getAllLocations", false, false);
  const { data: evsesStatus } = useDropdown("evses_status");
  const { data: partyIds } = useDropdown("ocpi_party_ids");

  const loadLocations = () => {
    const params = {
      limit: 50,
      offset: 0,
      status: 'All Status',
      partyId: 'All PartyId'
    };

    // Add search parameter if search term exists
    if (searchQuery.trim()) {
      params.search = searchQuery.trim();
    }

    // Add status filter if not 'All Status'
    if (statusFilter !== 'All Status') {
      params.status = statusFilter;
    }

    if (partyFilter !== 'All PartyId') {
      params.partyId = partyFilter;
    }

    fetchLocations(params);
  }
  useEffect(() => {
    // Filter locations based on search and filters
    loadLocations();
  }, [searchQuery, statusFilter, partyFilter]);

  const handleView = (location) => {
    setSelectedLocation(location);
    setShowDetailsModal(true);
  };

  const handleEdit = (location) => {
    setSelectedLocation(location);
    setIsEditMode(true);
    setShowFormModal(true);
  };

  const handleDelete = async (locationId) => {
    console.log('Delete location:', locationId);
    // TODO: Implement delete API call
  //  setLocations(prev => prev.filter(loc => loc.id !== locationId));
  };

  const handleSaveLocation = async (formData) => {
    console.log('Save location:', formData);
    // TODO: Implement save API call
    if (isEditMode) {
      // setLocations(prev => prev.map(loc => 
      //   loc.id === selectedLocation.id ? { ...loc, ...formData } : loc
      // ));
    } else {
      const newLocation = {
        ...formData,
        id: `LOC-${Date.now()}`,
        evseCount: 0,
        connectorCount: 0,
        availableEvseCount: 0,
        lastUpdated: new Date().toISOString()
      };
      // setLocations(prev => [...prev, newLocation]);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPartyFilter('');
  };

  const handleLocationSelect = (locationId) => {
    setSelectedLocationIds(prev => 
      prev.includes(locationId) 
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          OCPI Locations Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and monitor charging locations across your OCPI network
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 4, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              OCPI Locations
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <Icon icon="eva:options-2-fill" style={{ fontSize: '20px', color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Filters & Search
            </Typography>
          </Box>

          {/* Filter Controls */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* Search Box */}
              <TextField
                size="small"
                placeholder="Search locations..."
                onChange={(e) => 
                  debounceFunc(() => {
                    setSearchQuery(e.target.value);
                  }, 800)()
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon icon="eva:search-fill" />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  minWidth: 250,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'background.paper'
                  }
                }}
              />

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                >
                  {evsesStatus && evsesStatus.map(status => (
                    <MenuItem key={status.id} value={status.value}>{status.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Party Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Party ID</InputLabel>
                <Select
                  value={partyFilter}
                  label="Party ID"
                  onChange={(e) => setPartyFilter(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper'
                    }
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {partyIds && partyIds.map(partyId => (
                    <MenuItem key={partyId.id} value={partyId.value}>{partyId.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Clear Filters */}
              {(searchQuery || statusFilter || partyFilter) && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<Icon icon="eva:refresh-fill" />}
                  sx={{
                    borderColor: 'text.secondary',
                    color: 'text.secondary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main'
                    }
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>

            {/* Add New Location Button */}
            {/* <Button
              variant="contained"
              color="primary"
              startIcon={<Icon icon="eva:plus-fill" />}
              onClick={() => {
                setIsEditMode(false);
                setSelectedLocation(null);
                setShowFormModal(true);
              }}
              sx={{ 
                fontWeight: 600,
                textTransform: 'none',
                px: 3,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 2
                }
              }}
            >
              Add New Location
            </Button> */}
          </Box>

          {/* Active Filters Display */}
          {(searchQuery || statusFilter || partyFilter) && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                Active filters:
              </Typography>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery('')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'primary.main'
                    }
                  }}
                />
              )}
              {statusFilter && (
                <Chip
                  label={`Status: ${statusFilter}`}
                  onDelete={() => setStatusFilter('')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'primary.main'
                    }
                  }}
                />
              )}
              {partyFilter && (
                <Chip
                  label={`Party: ${partyFilter}`}
                  onDelete={() => setPartyFilter('')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    '& .MuiChip-deleteIcon': {
                      color: 'primary.main'
                    }
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Locations List */}
      <LocationList
        locations={locations}
        selectedLocationIds={selectedLocationIds}
        onLocationSelect={handleLocationSelect}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isLoading={isLoading}
        emptyMessage="No locations found matching your criteria"
      />
      
      {/* Modals */}
      <LocationDetails
        open={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedLocation(null);
        }}
        location={selectedLocation}
      />
      
      <LocationForm
        open={showFormModal}
        onClose={() => {
          setShowFormModal(false);
          setSelectedLocation(null);
          setIsEditMode(false);
        }}
        onSave={handleSaveLocation}
        location={selectedLocation}
        isEdit={isEditMode}
      />
    </Container>
  );
};

export default OCPILocationsPage;
